// Services.jsx
import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import {
  FaMobileAlt,
  FaLaptopCode,
  FaServer,
  FaLayerGroup,
  FaTools,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import ContactModal from "../components/ContactModal";

const gradientBG = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

const Section = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(-45deg, #0d0d0d, #1a1a1a, #111, #222);
  background-size: 300% 300%;
  animation: ${gradientBG} 12s ease infinite;
  text-align: center;
`;

const Title = styled.h2`
  font-size: 2.8rem;
  background: linear-gradient(135deg, #ff7a18, #ff0066);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 4rem;
`;

const Grid = styled.div`
  display: grid;
  gap: 2.5rem;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: transform 0.4s ease, box-shadow 0.4s ease;
  text-align: left;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 12px 30px rgba(255, 122, 24, 0.25);
  }
`;

const IconWrapper = styled.div`
  font-size: 2.5rem;
  color: #ff7a18;
  margin-bottom: 1rem;
  transition: color 0.3s ease;

  ${Card}:hover & {
    color: #ff0066;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.8rem;
`;

const Description = styled.p`
  color: #ccc;
  line-height: 1.6;
  font-size: 0.95rem;
  margin-bottom: 1.2rem;
`;

const ActionButton = styled.button`
  margin-top: auto;
  padding: 10px 18px;
  border-radius: 10px;
  text-align: center;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  border: none;
  transition: all 0.3s;

  ${({ type }) =>
    type === "quote"
      ? `
    background: linear-gradient(135deg, #ff7a18, #ff0066);
    color: #fff;
    &:hover {
      background: linear-gradient(135deg, #ff5722, #e91e63);
    }
  `
      : `
    background: linear-gradient(135deg, #00d4ff, #00bcd4);
    color: #1e1e2f;
    &:hover {
      background: linear-gradient(135deg, #0097a7, #00acc1);
      color: #fff;
    }
  `}
`;

const ActionsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-top: auto;
`;

export default function Services() {
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [modalType, setModalType] = useState("quote");
  const [preselectedService, setPreselectedService] = useState("");

  const openModal = (type, service) => {
    setModalType(type);
    setPreselectedService(service);
    setIsContactOpen(true);
  };

  const services = [
    {
      icon: <FaMobileAlt />,
      title: "Mobile App Development",
      desc: "Custom mobile apps using React Native & Flutter — designed to scale with your business.",
      actions: [
        { label: "Get Quote", type: "quote", modal: true, service: "mobile" },
      ],
    },
    {
      icon: <FaLaptopCode />,
      title: "Web Development",
      desc: "High-performance, responsive websites with cutting-edge tech and modern UI/UX.",
      actions: [
        { label: "Get Quote", type: "quote", modal: true, service: "web" },
      ],
    },
    {
      icon: <FaServer />,
      title: "Backend Solutions",
      desc: "APIs, databases, and cloud setups with Node.js, GraphQL, Firebase, and AWS.",
      actions: [
        { label: "Get Quote", type: "quote", modal: true, service: "backend" },
      ],
    },
    {
      icon: <FaLayerGroup />,
      title: "Courses & Mentorship",
      desc: "Practical, budget-friendly courses with real-world projects — learn React Native, SQL, and more.",
      actions: [
        { label: "View Courses", type: "courses", link: "/courses" },
        {
          label: "Ask for Mentorship",
          type: "mentorship",
          modal: true,
          service: "courses",
        },
      ],
    },
    {
      icon: <FaTools />,
      title: "Portfolio & Career Support",
      desc: "Get guidance, improve your developer profile, and showcase projects that land opportunities.",
      actions: [
        { label: "Get Quote", type: "quote", modal: true, service: "career" },
      ],
    },
  ];

  return (
    <Section>
      <Title>Our Freelancing Services</Title>
      <Grid>
        {services.map((service, index) => (
          <Card key={index}>
            <IconWrapper>{service.icon}</IconWrapper>
            <CardTitle>{service.title}</CardTitle>
            <Description>{service.desc}</Description>
            <ActionsWrapper>
              {service.actions.map((action, i) =>
                action.modal ? (
                  <ActionButton
                    key={i}
                    type={action.type}
                    onClick={() => openModal(action.type, action.service)}
                  >
                    {action.label}
                  </ActionButton>
                ) : (
                  <Link
                    to={action.link}
                    key={i}
                    style={{ textDecoration: "none" }}
                  >
                    <ActionButton type={action.type}>
                      {action.label}
                    </ActionButton>
                  </Link>
                )
              )}
            </ActionsWrapper>
          </Card>
        ))}
      </Grid>

      <ContactModal
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
        modalType={modalType}
        preselectedService={preselectedService}
      />
    </Section>
  );
}
